import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Phone, MessageSquare, Star, CheckCircle2, MapPin } from "lucide-react";
import { MaskedLines } from "./MaskedLines";
import { Swoosh } from "./Swoosh";
import { Logo } from "./Logo";
import { BIZ } from "@/content";

const CHECKS = ["Plumbing & Gas Specialists", "Exceptional Customer Care", "Quality Workmanship", "Rapid Response"];

const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

const HeroPhoto = ({ sectionRef }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div {...fade(0.5)} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[1.75rem] rounded-tl-[6rem] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] ring-1 ring-white/20 lg:aspect-[4/5] lg:rounded-tl-[11rem]">
          <motion.img style={{ y: imgY }} src="/images/hero-plumber.jpg" alt="Plumberoo plumber standing in front of the branded Plumberoo van"
            className="absolute inset-0 h-[115%] w-full -translate-y-[7%] object-cover object-[22%_center]" data-testid="hero-photo" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,transparent_35%,rgba(11,31,66,0.55))]" />
        </div>
        <motion.div style={{ y: cardY, translateZ: 60 }}
          className="absolute -bottom-6 -left-4 rounded-2xl bg-white p-4 shadow-2xl sm:-left-10 sm:p-5" data-testid="hero-licence-card">
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-slate-600">Licensed &amp; insured</p>
          <p className="font-display text-3xl font-extrabold text-[#0F2B5B]">{BIZ.licence}</p>
          <div className="mt-1 flex gap-0.5 text-[#FFB400]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
        </motion.div>
        <p className="font-script absolute -right-1 top-6 rotate-[-8deg] text-3xl leading-none text-white drop-shadow-lg sm:right-4 sm:text-4xl" style={{ transform: "translateZ(40px) rotate(-8deg)" }}>
          Real people.<br />Real service.
          <span className="mt-1 block h-1 w-28 rounded-full bg-[#FF6B00]" />
        </p>
      </motion.div>
    </motion.div>
  );
};

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain bg-brand relative overflow-hidden pb-24 pt-32 text-white lg:pb-28 lg:pt-36">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_10%_0%,#2A8BEA_0%,transparent_55%)]" />
      <Swoosh className="absolute -right-24 top-0 h-full w-[70%] opacity-90 lg:-right-10 lg:w-[55%]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 lg:grid-cols-12 lg:gap-10 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div {...fade(0)} className="mb-7">
            <Logo light className="h-20 sm:h-24 lg:h-28" />
          </motion.div>
          <motion.div {...fade(0.05)} className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur-md">
              <MapPin className="h-4 w-4 text-[#FFB400]" /> Your local Perth plumber
            </span>
            <span className="font-cond inline-flex items-center rounded-full bg-[#FF6B00] px-4 py-1.5 text-base font-extrabold uppercase tracking-[0.14em] text-white shadow-lg" data-testid="hero-licence-badge">
              Licence {BIZ.licence}
            </span>
          </motion.div>
          <MaskedLines
            lines={["Plumbing", "Done Right.", <span key="w" className="text-flame">First Time,</span>, <span key="r" className="text-flame">Every Time.</span>]}
            className="font-display mt-7 text-[3.1rem] font-extrabold leading-[0.9] tracking-[-0.035em] sm:text-7xl lg:text-[5.5rem]"
          />
          <motion.p {...fade(0.75)} className="mt-7 max-w-xl text-base text-white/85 md:text-lg">
            Professional plumbing maintenance, repairs and renovations across Perth — from blocked drains to hot water and gas.
          </motion.p>
          <motion.ul {...fade(0.9)} className="mt-9 grid max-w-xl gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="hero-checklist">
            {CHECKS.map((c) => (
              <li key={c} className="font-cond flex items-center gap-3 text-lg font-bold tracking-wide sm:text-xl">
                <CheckCircle2 className="h-6 w-6 shrink-0 text-[#FFB21E]" strokeWidth={2.4} /> {c}
              </li>
            ))}
          </motion.ul>
          <motion.div {...fade(1.05)} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a href={BIZ.tel} data-testid="hero-call-button"
              className="shimmer group inline-flex items-center justify-center gap-3 rounded-full bg-[#FF6B00] px-8 py-4 font-bold uppercase tracking-[0.08em] shadow-[0_20px_40px_-15px_rgba(255,107,0,0.8)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#E05A00]">
              <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Call {BIZ.phone}
            </a>
            <button onClick={() => onQuote()} data-testid="hero-quote-button"
              className="inline-flex items-center justify-center gap-3 rounded-full border-2 border-white/80 px-8 py-4 font-bold uppercase tracking-[0.08em] transition-[background-color,color] duration-300 hover:bg-white hover:text-[#0F2B5B]">
              <MessageSquare className="h-5 w-5" /> Request a service
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5"><HeroPhoto sectionRef={ref} /></div>
      </div>
    </section>
  );
};
